/**
 * Safe Area Provider
 * Uses native Android plugin for precise safe area insets
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import SafeArea from '@/plugins/SafeAreaPlugin';
import { logger } from '@/lib/logger';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const useSafeArea = (): SafeAreaInsets => {
  const [insets, setInsets] = useState<SafeAreaInsets>({ 
    top: 0, 
    bottom: 0, 
    left: 0, 
    right: 0 
  });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      // Fallback para iOS/Android na web (simulação de safe area)
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        setInsets({ top: 47, bottom: 34, left: 0, right: 0 }); // iPhone com notch
        logger.info('Safe area fallback applied for iOS web', { top: 47, bottom: 34 });
        return;
      } else if (platform === 'android') {
        setInsets({ top: 24, bottom: 0, left: 0, right: 0 }); // Android status bar
        logger.info('Safe area fallback applied for Android web', { top: 24 });
        return;
      }
      return; // Web desktop: sem safe area
    }

    const loadInsets = async () => {
      try {
        const result = await SafeArea.getInsets();
        
        logger.info('Safe area insets loaded from native plugin', result);
        
        setInsets(result);
        
        // Injetar variáveis CSS globais
        document.documentElement.style.setProperty('--sat', `${result.top}px`);
        document.documentElement.style.setProperty('--sab', `${result.bottom}px`);
        document.documentElement.style.setProperty('--sal', `${result.left}px`);
        document.documentElement.style.setProperty('--sar', `${result.right}px`);
      } catch (error) {
        logger.error('Failed to get safe area insets from plugin', error);
        
        // Fallback: usar valores CSS env() se disponível
        const topCss = getComputedStyle(document.documentElement)
          .getPropertyValue('--sat').trim();
        const bottomCss = getComputedStyle(document.documentElement)
          .getPropertyValue('--sab').trim();
        
        const fallbackInsets = {
          top: topCss ? parseInt(topCss) : 0,
          bottom: bottomCss ? parseInt(bottomCss) : 0,
          left: 0,
          right: 0
        };
        
        logger.warn('Using fallback safe area insets', fallbackInsets);
        setInsets(fallbackInsets);
      }
    };

    loadInsets();
    
    // Recalcular em resize (rotação, teclado, etc)
    const handleResize = () => {
      logger.info('Resize detected, recalculating safe area');
      loadInsets();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return insets;
};
